// Overwrites the displayed price for configured-safe cart lines with the real server-computed
// total, since the cart line's actual Shopify variant is a cheap shared placeholder (its own
// price is never what gets charged — see extensions/theme-extension/blocks/
// product-configurator.liquid's handleAddToCartDraftOrder). Checkout itself is already
// correct regardless of this script (createCheckoutDraftOrder always re-prices server-side
// from the saved selection, never from cart line data) — this only fixes what the shopper
// sees on the cart page/drawer before checking out.
//
// Reads price data via /cart.js (the JSON API) rather than scraping the DOM for a "Total
// Price" property, because cart__main.liquid's own template explicitly hides any property
// literally named "Total Price" from ever rendering (see git history) — a DOM-text-search
// approach can never see a property the template refuses to render.
(function () {
  function parseMoneyText(text) {
    const match = text.match(/[\d,]+\.\d{2}/);
    return match ? match[0] : null;
  }

  // Replaces only the numeric portion of already-rendered money text, preserving whatever
  // currency symbol/suffix format the shop's money_format setting already produced (e.g.
  // "$1,234.56" or "$1,234.56 USD") without needing to know that format ourselves.
  function setMoneyText(el, newAmountCents) {
    const formatted = (newAmountCents / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const current = el.textContent || '';
    const currentAmount = parseMoneyText(current);
    if (currentAmount) {
      el.textContent = current.replace(currentAmount, formatted);
    } else {
      el.textContent = '$' + formatted;
    }
    // Reveals the price — see the .configurator-price-loading skeleton applied server-side
    // in cart__main.liquid/cart.ajax.liquid so the stale/placeholder price is never shown.
    el.classList.remove('configurator-price-loading');
  }

  function updateCartPriceDisplay() {
    fetch('/cart.js')
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        let subtotalDeltaCents = 0;
        // Tracks whether the subtotal's .configurator-price-loading skeleton (added
        // server-side whenever any cart line needs a price correction) needs resolving —
        // previously this only happened `if (subtotalDeltaCents !== 0)`, so on the rare
        // occasion the real total happened to already match cart.total_price exactly (delta
        // of 0), the skeleton was never removed and shimmered forever even though every other
        // price on the page updated fine.
        let hasConfiguredPricing = false;

        (cart.items || []).forEach(function (item) {
          const props = item.properties || {};
          if (props._configurator !== 'true' || !props._calculatedPrice) return;

          const realUnitPriceCents = Math.round(parseFloat(props._calculatedPrice) * 100);
          if (!realUnitPriceCents || isNaN(realUnitPriceCents)) return;

          hasConfiguredPricing = true;
          const realLinePriceCents = realUnitPriceCents * item.quantity;
          subtotalDeltaCents += realLinePriceCents - item.line_price;

          const row = document.querySelector('[data-cart-item="' + item.key + '"]');
          if (!row) return;

          // Single-quantity configured lines (the normal case): every .money span in the row
          // represents the same amount (unit price == line total), so it's correct to set
          // them all to the real total. For quantity > 1, unit-price-looking spans still get
          // the full line total here — acceptable since configured items aren't expected to
          // carry quantity > 1 in practice (this store has no quantity selector wired for
          // them), and getting the line total right is what matters most.
          row.querySelectorAll('.money').forEach(function (moneyEl) {
            setMoneyText(moneyEl, realLinePriceCents);
          });
        });

        if (hasConfiguredPricing) {
          const correctedSubtotalCents = cart.total_price + subtotalDeltaCents;
          document
            .querySelectorAll('.cart__subtotal .money, .ajax-cart__subtotal .money')
            .forEach(function (el) {
              setMoneyText(el, correctedSubtotalCents);
            });
        }
      })
      .catch(function (error) {
        console.error('configurator-cart-price-display: failed to update cart prices', error);
      });
  }

  document.addEventListener('DOMContentLoaded', updateCartPriceDisplay);
  document.addEventListener('cart:updated', function () {
    setTimeout(updateCartPriceDisplay, 300);
  });
  document.addEventListener('cart:refresh', function () {
    setTimeout(updateCartPriceDisplay, 300);
  });

  // Cart drawer content can be injected well after DOMContentLoaded (e.g. opened via AJAX
  // fetch of cart.ajax.liquid) — watch for that content appearing and re-run.
  const observer = new MutationObserver(function (mutations) {
    const added = mutations.some(function (m) {
      return m.addedNodes.length > 0;
    });
    if (added) {
      clearTimeout(window.__configuratorCartPriceDebounce);
      window.__configuratorCartPriceDebounce = setTimeout(updateCartPriceDisplay, 300);
    }
  });
  document.addEventListener('DOMContentLoaded', function () {
    observer.observe(document.body, { childList: true, subtree: true });
  });

  window.updateConfiguredCartPriceDisplay = updateCartPriceDisplay;
})();
